import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { useAuth } from '../../context/AuthContext.js';
import userService from '../../services/UserService.js';
import salaryService from '../../services/SalaryService.js';
import attendanceService from '../../services/AttendanceService.js';
import alertService from '../../services/AlertService.js';
import AlertModal from '../common/AlertModal.js';
import Alert from '../common/Alert.js';
import logo from "../../static/images/logo.png";
import dqLogo from "../../static/images/logo_DQ.png"; // ✅ NEW LOGO

const Header = ({ sidebarOpen = false, onOpenSidebar }) => {
  const navigate = useNavigate();
  const { user, logout, loading } = useAuth();
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [fullUserData, setFullUserData] = useState(null);
  const [currentSalary, setCurrentSalary] = useState(null);
  const [loadingUserData, setLoadingUserData] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Attendance states
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [workDuration, setWorkDuration] = useState(0);
  const [loginTime, setLoginTime] = useState(null);
  const [attendanceDate, setAttendanceDate] = useState(null);

  useEffect(() => {
    if (showProfileModal && user?.user_id && !fullUserData) {
      fetchFullUserData();
      fetchCurrentSalary();
    }
  }, [showProfileModal, user?.user_id]);

  useEffect(() => {
    if (user?.user_id) {
      // Fetch immediately
      fetchUnreadCount();
      
      // Set up polling every 5 seconds
      const interval = setInterval(() => {
        fetchUnreadCount();
      }, 10000);

      return () => clearInterval(interval);
    }
  }, [user?.user_id]);

  const fetchUnreadCount = async () => {
    try {
      const response = await alertService.getUnreadCount(user.user_id);
      setUnreadCount(response.unread_count || 0);
    } catch (err) {
      console.error('Failed to fetch unread count:', err);
    }
  };

  // Check active attendance status on component mount
  useEffect(() => {
    if (user?.user_id) {
      checkActiveAttendance();
    }
  }, [user?.user_id]);

  // Timer effect to count work duration
  useEffect(() => {
    let interval;
    if (isLoggedIn && loginTime) {
      interval = setInterval(() => {
        const now = new Date();
        const login = new Date(loginTime);
        const duration = Math.floor((now - login) / 1000); // Duration in seconds
        setWorkDuration(duration);
      }, 1000); // Update every second
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isLoggedIn, loginTime]);

  const handleAlertClick = () => {
    setShowAlertModal(true);
  };

  const handleCloseAlertModal = () => {
    setShowAlertModal(false);
    // Refresh count after closing modal
    fetchUnreadCount();
  };

  const getCurrentPosition = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by your browser'));
        return;
      }

      // Try high accuracy first
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy
          });
        },
        (error) => {
          // If high accuracy fails, try with lower accuracy as fallback
          if (error.code === error.TIMEOUT) {
            console.log('High accuracy timeout, trying with lower accuracy...');
            navigator.geolocation.getCurrentPosition(
              (position) => {
                resolve({
                  latitude: position.coords.latitude,
                  longitude: position.coords.longitude,
                  accuracy: position.coords.accuracy
                });
              },
              (fallbackError) => {
                let errorMessage = 'Unable to retrieve location';
                
                switch(fallbackError.code) {
                  case fallbackError.PERMISSION_DENIED:
                    errorMessage = 'Location permission denied. Please enable location access in your browser settings.';
                    break;
                  case fallbackError.POSITION_UNAVAILABLE:
                    errorMessage = 'Location information unavailable. Please check your device settings.';
                    break;
                  case fallbackError.TIMEOUT:
                    errorMessage = 'Location request timed out. Please try again or check your GPS settings.';
                    break;
                }
                
                reject(new Error(errorMessage));
              },
              {
                enableHighAccuracy: false,  // Lower accuracy but faster
                timeout: 15000,
                maximumAge: 30000  // Accept cached position up to 30 seconds old
              }
            );
          } else {
            let errorMessage = 'Unable to retrieve location';
            
            switch(error.code) {
              case error.PERMISSION_DENIED:
                errorMessage = 'Location permission denied. Please enable location access in your browser settings.';
                break;
              case error.POSITION_UNAVAILABLE:
                errorMessage = 'Location information unavailable. Please check your device settings.';
                break;
              case error.TIMEOUT:
                errorMessage = 'Location request timed out. Please try again.';
                break;
            }
            
            reject(new Error(errorMessage));
          }
        },
        {
          enableHighAccuracy: true,  // Try GPS first
          timeout: 20000,  // Increased to 20 seconds
          maximumAge: 0
        }
      );
    });
  };

  const checkActiveAttendance = async () => {
    try {
      const response = await attendanceService.getActiveStatus();
      if (response.is_active && response.data) {
        setIsLoggedIn(true);
        setLoginTime(response.data.login_time);
        setAttendanceDate(response.data.attendance_date);
      } else {
        setIsLoggedIn(false);
        setLoginTime(null);
        setAttendanceDate(null);
        setWorkDuration(0);
      }
    } catch (err) {
      console.error('Failed to check attendance status:', err);
    }
  };

  const handleAttendanceLogin = async () => {
    try {
      setAttendanceLoading(true);
      
      // Get GPS location first
      Alert.info('Getting your location...');
      const position = await getCurrentPosition();
      
      // Send location data with login request
      const response = await attendanceService.loginAttendance(
        position.latitude, 
        position.longitude
      );
      
      setIsLoggedIn(true);
      setLoginTime(response.data.login_time);
      setAttendanceDate(response.data.attendance_date);
      setWorkDuration(0);
      Alert.success('Attendance logged in successfully!');
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to login attendance';
      Alert.error(errorMessage);
      console.error('Attendance login error:', err);
      
      // If there's already an active attendance, refresh the status
      if (err.response?.status === 400 && err.response?.data?.active_attendance) {
        checkActiveAttendance();
      }
    } finally {
      setAttendanceLoading(false);
    }
  };

  const handleAttendanceLogout = async () => {
    // Check if logging out on a different day
    const today = new Date().toISOString().split('T')[0];
    const loginDate = attendanceDate ? new Date(attendanceDate).toISOString().split('T')[0] : today;
    
    let confirmMessage = `You have worked for ${formatDuration(workDuration)}. Are you sure you want to logout?`;
    
    if (loginDate !== today) {
      const loginDateFormatted = new Date(attendanceDate).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
      confirmMessage = `You logged in on ${loginDateFormatted}. You have worked for ${formatDuration(workDuration)}. Are you sure you want to logout now?`;
    }

    const confirmed = await Alert.confirm(
      confirmMessage,
      'Logout Attendance'
    );

    if (confirmed) {
      try {
        setAttendanceLoading(true);
        
        // Get GPS location first
        Alert.info('Getting your location...');
        const position = await getCurrentPosition();
        
        // Send location data with logout request
        const response = await attendanceService.logoutAttendance(
          position.latitude, 
          position.longitude
        );
        
        setIsLoggedIn(false);
        setLoginTime(null);
        setAttendanceDate(null);
        setWorkDuration(0);
        Alert.success(`Logged out successfully! Total work time: ${response.data.work_hours_formatted}`);
      } catch (err) {
        Alert.error(err.response?.data?.error || err.message || 'Failed to logout attendance');
        console.error('Attendance logout error:', err);
      } finally {
        setAttendanceLoading(false);
      }
    }
  };

  // Get display date for login (handles different day scenario)
  const getLoginDisplayDate = () => {
    if (!attendanceDate) return '';
    const today = new Date().toISOString().split('T')[0];
    const loginDate = new Date(attendanceDate).toISOString().split('T')[0];
    
    if (loginDate !== today) {
      return new Date(attendanceDate).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short'
      });
    }
    return '';
  };

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours}h ${minutes}m ${secs}s`;
  };

  const fetchFullUserData = async () => {
    try {
      setLoadingUserData(true);
      const response = await userService.getById(user.user_id);
      setFullUserData(response.data);
    } catch (err) {
      console.error('Failed to load user details:', err);
    } finally {
      setLoadingUserData(false);
    }
  };

  const fetchCurrentSalary = async () => {
    try {
      const currentDate = new Date();
      const currentMonth = currentDate.toLocaleString('en-US', { month: 'long' }).toLowerCase();
      const currentYear = currentDate.getFullYear();
      
      const response = await salaryService.getAll(1, 10, {
        user_id: user.user_id,
        month: currentMonth,
        year: currentYear
      });
      
      if (response.data && response.data.length > 0) {
        setCurrentSalary(response.data[0]);
      }
    } catch (err) {
      console.error('Failed to load salary data:', err);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const getProfileImageUrl = (userData = user) => {
    if (userData?.profile_photo_base64) {
      return `data:image/jpeg;base64,${userData.profile_photo_base64}`;
    }
    if (userData?.user_id) {
      return userService.getProfilePhoto(userData.user_id);
    }
    return null;
  };

  const capitalizeStatus = (status) => {
    return status ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() : '';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const getSalaryStatusBadge = (status) => {
    const statusColors = {
      pending: 'bg-yellow-100 text-yellow-700',
      credited: 'bg-green-100 text-green-700',
      processing: 'bg-blue-100 text-brand',
      hold: 'bg-blue-100 text-blue-700',
      cancelled: 'bg-red-100 text-red-700'
    };
    
    return statusColors[status] || 'bg-gray-100 text-gray-700';
  };

  const handleProfileClick = () => {
    setShowProfileModal(true);
  };

  const closeModal = () => {
    setShowProfileModal(false);
  };

  return (
    <>
      <header className="bg-white shadow-card border-b border-gray-200 fixed lg:sticky top-0 z-50 w-full shrink-0">
        <div className="flex items-center justify-between gap-2 sm:gap-3 px-3 sm:px-4 lg:px-6 py-2 min-h-[56px] lg:min-h-[72px] w-full max-w-full overflow-hidden">
          {/* Menu + Logo — grouped on the left */}
          <div className="flex items-center gap-2 shrink-0 min-w-0 max-w-[50%] sm:max-w-none">
            {onOpenSidebar && !sidebarOpen && (
              <button
                type="button"
                onClick={onOpenSidebar}
                className="lg:hidden p-1.5 rounded-btn hover:bg-gray-100 transition-colors shrink-0"
                aria-label="Open menu"
              >
                <Menu className="w-5 h-5 text-secondary" />
              </button>
            )}
            <img
              src={logo}
              alt="Metro Logistics"
              className="h-8 sm:h-9 md:h-10 lg:h-14 w-auto object-contain object-left"
            />
          </div>

          {/* Actions — right, never wrap */}
          <div className="flex items-center justify-end gap-1 sm:gap-2 md:gap-3 shrink-0 flex-nowrap min-w-0">
            {loading ? (
              <div className="flex items-center gap-2 shrink-0">
                <div className="w-8 h-8 bg-gray-200 rounded-full animate-pulse" />
                <div className="space-y-1 hidden lg:block">
                  <div className="w-24 h-3 bg-gray-200 rounded animate-pulse" />
                  <div className="w-16 h-2 bg-gray-200 rounded animate-pulse" />
                </div>
              </div>
            ) : user ? (
              <>
                {/* Attendance */}
                {attendanceLoading ? (
                  <div className="flex items-center gap-1 px-2 py-1.5 bg-gray-100 rounded-btn shrink-0">
                    <div className="w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                    <span className="text-xs text-gray-500 hidden sm:inline">Loading...</span>
                  </div>
                ) : isLoggedIn ? (
                  <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
                    <div className="flex items-start gap-1.5">
                      <span className="mt-1 w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full bg-green-500 shrink-0 animate-pulse" />
                      <div className="flex flex-col leading-tight">
                        <span className="text-[11px] sm:text-xs md:text-sm font-semibold text-gray-800 whitespace-nowrap">
                          {formatDuration(workDuration)}
                        </span>
                        <span className="text-[10px] sm:text-xs text-gray-500 hidden sm:block whitespace-nowrap">
                          {attendanceDate
                            ? new Date(attendanceDate).toLocaleDateString('en-IN', {
                                day: '2-digit',
                                month: 'short',
                              })
                            : ''}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={handleAttendanceLogout}
                      className="px-2.5 sm:px-3 md:px-4 py-1.5 bg-danger hover:bg-red-600 text-white text-xs sm:text-sm font-semibold rounded-btn transition-colors shrink-0 whitespace-nowrap"
                      title="Logout Attendance"
                    >
                      OUT
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={handleAttendanceLogin}
                    className="flex items-center gap-1 px-2 sm:px-3 py-1.5 bg-success hover:bg-emerald-600 text-white text-xs sm:text-sm font-semibold rounded-btn transition-colors shrink-0 whitespace-nowrap"
                    title="Login Attendance"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>IN</span>
                  </button>
                )}

                {/* Notifications */}
                <button
                  onClick={handleAlertClick}
                  className="relative p-1.5 sm:p-2 hover:bg-gray-100 rounded-btn transition-colors shrink-0"
                  title="Notifications"
                >
                  <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                    />
                  </svg>
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-0.5 flex items-center justify-center">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </button>

                {/* Profile */}
                <button
                  onClick={handleProfileClick}
                  className="flex items-center gap-2 hover:bg-gray-50 rounded-btn p-1 sm:p-1.5 transition-colors shrink-0 min-w-0"
                >
                  <div className="relative shrink-0">
                    {getProfileImageUrl() ? (
                      <img
                        src={getProfileImageUrl()}
                        alt={user.name}
                        className="w-8 h-8 sm:w-9 sm:h-9 lg:w-10 lg:h-10 rounded-full object-cover border-2 border-gray-200"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = `data:image/svg+xml,${encodeURIComponent(`
                            <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
                              <rect width="40" height="40" fill="#e5e7eb"/>
                              <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="#6b7280" font-family="Arial" font-size="16" font-weight="bold">
                                ${user.name.charAt(0).toUpperCase()}
                              </text>
                            </svg>
                          `)}`;
                        }}
                      />
                    ) : (
                      <div className="w-8 h-8 sm:w-9 sm:h-9 lg:w-10 lg:h-10 bg-gray-200 rounded-full flex items-center justify-center border-2 border-gray-200">
                        <span className="text-gray-600 font-bold text-xs sm:text-sm">
                          {user.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="hidden xl:block text-left min-w-0 max-w-[140px]">
                    <p className="text-sm font-semibold text-dark truncate">{user.name}</p>
                    <p className="text-xs text-secondary truncate">{user.role}</p>
                  </div>
                </button>

                {/* System logout */}
                <button
                  onClick={handleLogout}
                  className="p-1.5 sm:p-2 hover:bg-red-50 text-danger rounded-btn transition-colors shrink-0"
                  title="Logout from System"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                </button>

                {/* DQ logo — desktop only */}
                <img
                  src={dqLogo}
                  alt="DQ Logo"
                  className="hidden xl:block h-7 w-auto object-contain shrink-0"
                />
              </>
            ) : (
              <button
                onClick={() => navigate('/login')}
                className="px-4 py-2 bg-brand hover:bg-brand-hover text-white text-sm font-semibold rounded-btn transition-colors shrink-0"
              >
                Login
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Profile Modal - (Keep existing modal code as is) */}
      {showProfileModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-start sm:items-center justify-center p-0 sm:p-4 overflow-y-auto">
          <div className="bg-white w-full sm:max-w-4xl sm:rounded-lg shadow-xl min-h-screen sm:min-h-0 sm:my-8 max-h-screen sm:max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-4 flex items-center justify-between z-10">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900">My Profile</h2>
              <button
                onClick={closeModal}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            {loadingUserData ? (
              <div className="flex items-center justify-center py-12 sm:py-20">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-brand mx-auto mb-4"></div>
                  <p className="text-gray-600 text-sm sm:text-base">Loading profile...</p>
                </div>
              </div>
            ) : fullUserData ? (
              <>
                {/* Profile Header Section */}
                <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-4 sm:p-8">
                  <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
                    <div className="w-20 h-20 sm:w-32 sm:h-32 rounded-full border-4 border-white overflow-hidden bg-white flex items-center justify-center flex-shrink-0">
                      <img
                        src={getProfileImageUrl(fullUserData)}
                        alt={fullUserData.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = `data:image/svg+xml,${encodeURIComponent(`
                            <svg xmlns="http://www.w3.org/2000/svg" width="128" height="128" viewBox="0 0 128 128">
                              <rect width="128" height="128" fill="#e5e7eb"/>
                              <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="#6b7280" font-family="Arial" font-size="48" font-weight="bold">
                                ${fullUserData.name.charAt(0).toUpperCase()}
                              </text>
                            </svg>
                          `)}`;
                        }}
                      />
                    </div>
                    <div className="flex-1 text-center sm:text-left">
                      <h2 className="text-xl sm:text-3xl font-bold text-white mb-2">{fullUserData.name}</h2>
                      <p className="text-blue-100 text-base sm:text-lg mb-3">{fullUserData.role_name}</p>
                      <div className="flex items-center justify-center sm:justify-start gap-3">
                        <span className={`px-3 sm:px-4 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-semibold ${
                          fullUserData.status === 'active' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {capitalizeStatus(fullUserData.status)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Details Sections */}
                <div className="p-4 sm:p-8 space-y-6 sm:space-y-8">
                  {/* Current Month Salary Status */}
                  {currentSalary && (
                    <div>
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 pb-2 border-b-2 border-brand">
                        Current Salary Status ({currentSalary.month_display} {currentSalary.year})
                      </h3>
                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 sm:p-6 rounded-lg border border-blue-200">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Net Salary</label>
                            <p className="text-lg sm:text-xl font-bold text-brand">{formatCurrency(currentSalary.net_salary)}</p>
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Payment Status</label>
                            <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getSalaryStatusBadge(currentSalary.status)}`}>
                              {currentSalary.status.toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Payment Date</label>
                            <p className="text-sm sm:text-base text-gray-900 font-medium">{formatDate(currentSalary.payment_date)}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Basic Information */}
                  <div>
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 pb-2 border-b-2 border-brand">
                      Basic Information
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-1">Username</label>
                        <p className="text-sm sm:text-base text-gray-900 font-medium break-all">{fullUserData.username || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-1">Full Name</label>
                        <p className="text-sm sm:text-base text-gray-900 font-medium">{fullUserData.name}</p>
                      </div>
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-1">Email</label>
                        <p className="text-sm sm:text-base text-gray-900 font-medium break-all">{fullUserData.email}</p>
                      </div>
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-1">Date of Birth</label>
                        <p className="text-sm sm:text-base text-gray-900 font-medium">{formatDate(fullUserData.dob)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div>
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 pb-2 border-b-2 border-brand">
                      Contact Information
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-1">Contact Number</label>
                        <p className="text-sm sm:text-base text-gray-900 font-medium">{fullUserData.contact}</p>
                      </div>
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-1">Branch</label>
                        <p className="text-sm sm:text-base text-gray-900 font-medium">{fullUserData.city}</p>
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-1">Location/Address</label>
                        <p className="text-sm sm:text-base text-gray-900 font-medium">{fullUserData.location}</p>
                      </div>
                    </div>
                  </div>

                  {/* Document Information */}
                  <div>
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 pb-2 border-b-2 border-brand">
                      Document Information
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-1">Document Type</label>
                        <p className="text-sm sm:text-base text-gray-900 font-medium">
                          {fullUserData.document_type ? fullUserData.document_type.replace(/_/g, ' ').toUpperCase() : 'N/A'}
                        </p>
                      </div>
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-1">Document Number</label>
                        <p className="text-sm sm:text-base text-gray-900 font-medium break-all">{fullUserData.document_number || 'N/A'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Role and Status */}
                  <div>
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 pb-2 border-b-2 border-brand">
                      Role and Status
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-1">Role</label>
                        <p className="text-sm sm:text-base text-gray-900 font-medium">{fullUserData.role_name}</p>
                      </div>
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-1">Status</label>
                        <p className="text-sm sm:text-base text-gray-900 font-medium">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            fullUserData.status === 'active' 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {capitalizeStatus(fullUserData.status)}
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* System Information */}
                  <div>
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 pb-2 border-b-2 border-brand">
                      System Information
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-1">User ID</label>
                        <p className="text-sm sm:text-base text-gray-900 font-medium break-all">{fullUserData.user_id}</p>
                      </div>
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-1">Created By</label>
                        <p className="text-sm sm:text-base text-gray-900 font-medium">{fullUserData.created_by || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-1">Created At</label>
                        <p className="text-sm sm:text-base text-gray-900 font-medium">{formatDate(fullUserData.created_at)}</p>
                      </div>
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-1">Last Updated</label>
                        <p className="text-sm sm:text-base text-gray-900 font-medium">{formatDate(fullUserData.updated_at)}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="sticky bottom-0 bg-gray-50 px-4 sm:px-8 py-4 sm:py-6 border-t">
                  <button
                    onClick={closeModal}
                    className="w-full px-4 sm:px-6 py-2.5 sm:py-3 border border-gray-300 text-gray-900 rounded-lg font-semibold hover:bg-gray-100 transition-colors text-sm sm:text-base"
                  >
                    Close
                  </button>
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}

      {/* Alert Modal */}
      <AlertModal 
        isOpen={showAlertModal} 
        onClose={handleCloseAlertModal}
        userId={user?.user_id}
      />
    </>
  );
};

export default React.memo(Header);